// 主产品组件
class MainProductV2 extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.productId = this.dataset.productId;
    this.productUrl = this.dataset.productUrl;
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.currentSelections = {};
    this.matchedVariant = this.variants.find(v => v.selected);
    this.data = { main: null, complementary: null }; // 主/互补产品数据统一存
    this.subscribes = {}; // 统一管理所有订阅

    // 一次性订阅所有事件
    this.subscribes.variant = subscribe('variant:option-changed', this.handleOptionChange.bind(this));
    this.subscribes.mainQty = subscribe('main:quantity-changed', d => { this.data.main = d; this.updateBtns(); });
    this.subscribes.comp = subscribe('complementary:price-changed', d => { this.data.complementary = d; this.updateBtns(); });

    // 初始化选中项+首次更新按钮
    this.matchedVariant && this.matchedVariant.options.forEach((opt, i) => this.currentSelections[`option${i+1}`] = opt);
    this.updateBtns();
  }

  // 变体切换逻辑
  handleOptionChange({ optionName, optionValue }) {
    this.currentSelections[optionName] = optionValue;
    this.matchedVariant = this.variants.find(v => v.options.every((opt, i) => this.currentSelections[`option${i+1}`] === opt));
    if (!this.matchedVariant) return;
    history.replaceState({}, '', `${this.productUrl}?variant=${this.matchedVariant.id}`);
    this.fetchUpdatedSection(this.matchedVariant.id);
    this.updateBtns();
  }

  updateBtns() {
    const addCartBtns = [...this.querySelectorAll('add-cart-button')];
    const buyNowBtns = [...this.querySelectorAll('buy-now-button')];
    const allBtns = [...addCartBtns, ...buyNowBtns].filter(Boolean);
    if (allBtns.length === 0) return;

    // 处理主产品数据（无数据则用变体ID+默认数量1）
    const mainId = this.data.main?.variantId || this.matchedVariant?.id;
    const mainQty = this.data.main?.quantity > 0 ? this.data.main.quantity : 1;
    const [vIds, qtys] = mainId ? [[mainId], [mainQty]] : [[], []];

    // 处理互补产品数据（仅过滤有效项：有ID+数量>0）
    this.data.complementary?.checkedItems.forEach(item => {
      item.variantId && item.quantity > 0 && (vIds.push(item.variantId), qtys.push(item.quantity));
    });

    // 批量更新所有按钮的data属性（一行循环搞定，不管多少个按钮）
    const [vIdStr, qtyStr] = [vIds.join(','), qtys.join(',')];
    allBtns.forEach(btn => { btn.dataset.variantId = vIdStr; btn.dataset.quantity = qtyStr; });
  }

  // 局部刷新
  fetchUpdatedSection(variantId) {
    fetch(`${this.productUrl}?variant=${variantId}&section_id=${this.sectionId}`)
      .then(res => res.text())
      .then(html => {
        const newContent = new DOMParser().parseFromString(html, 'text/html').getElementById(`main-product-v2-${this.sectionId}`);
        newContent && this.replaceWith(newContent);
      });
  }

  // 批量销毁所有订阅
  disconnectedCallback() {
    Object.values(this.subscribes).forEach(unsub => unsub && unsub());
  }
}
customElements.define('main-product-v2', MainProductV2);


// 产品轮播组件
class ProductMediaGallery extends HTMLElement {
    constructor() {
        super();
        this.mainSwiper = null;
        this.thumbSwiper = null;
    }

    connectedCallback() {
        this.initSwipers();
        this.setupEventListeners();
    }

    initSwipers() {
        const enableSlider = this.dataset.enableSlider === 'true';
        if (!enableSlider) return;

        this.thumbSwiper = new Swiper(this.querySelector('.product-media-gallery__thumbs-swiper'), {
            spaceBetween: 8,
            freeMode: true,
            watchSlidesProgress: true,
            breakpoints: {
                1200: {
                    direction: 'horizontal',
                    slidesPerView: 5,
                },
                810: {
                    direction: 'horizontal',
                    slidesPerView: 3,
                },
            },
        });

        this.mainSwiper = new Swiper(this.querySelector('.product-media-gallery__main'), {
            initialSlide: parseInt(this.dataset.initialSlide) || 0,
            loop: this.dataset.sliderLoop === 'true',
            speed: 500,
            autoplay:
                this.dataset.sliderAutoplay === 'true'
                    ? {
                        delay: parseInt(this.dataset.sliderInterval) || 5000,
                        disableOnInteraction: false,
                    }
                    : false,
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
            thumbs: {
                swiper: this.thumbSwiper,
            },
            on: {
                init: (swiper) => {
                    this.updatePaginaion(swiper);
                },
                slideChange: (swiper) => {
                    this.updatePaginaion(swiper);
                }
            }
        });
    }

    updatePaginaion(swiper) {
        const currentEl = this.querySelector('.swiper-pagination-current');
        const totalEl = this.querySelector('.swiper-pagination-total');
        
        if (currentEl && totalEl) {
            currentEl.textContent = swiper.realIndex + 1;
            
            if (swiper.initialized) {
                totalEl.textContent = swiper.slides.length;
            }
        }
    }

    setupEventListeners() {
        window.addEventListener('resize', () => {
            if (this.thumbSwiper) {
                this.thumbSwiper.update();
            }
        });

        this.querySelectorAll('.video-thumb-btn').forEach((btn) => {
            btn.addEventListener('click', () => {
                this.openVideoDialog(btn.dataset.mediaId, btn.dataset.mediaType, btn.dataset.videoId, btn.dataset.videoSrc);
            });
        });

        const dialog = this.querySelector('.product-video-dialog');
        if (dialog) {
            const container = dialog.querySelector('.video-player-container');
            dialog.querySelector('.video-dialog-close').addEventListener('click', () => {
                dialog.close();
                container.innerHTML = '';
            });

            dialog.addEventListener('click', (e) => {
                if (e.target === dialog) {
                    dialog.close();
                    container.innerHTML = '';
                }
            });
        }
    }

    openVideoDialog(mediaId, mediaType, videoId, videoSrc) {
        const dialog = this.querySelector('.product-video-dialog');
        const container = dialog.querySelector('.video-player-container');

        container.innerHTML = '';

        if (mediaType === 'video' && videoSrc) {
            container.innerHTML = `
          <video controls autoplay loop muted>
            <source src="${videoSrc}" type="video/mp4">
            您的浏览器不支持 HTML5 视频
          </video>
        `;
        } else if (mediaType === 'external_video' && videoId) {
            container.innerHTML = `
          <iframe
            src="https://www.youtube.com/embed/${videoId}?autoplay=1&muted=1" 
            frameborder="0" 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
            allowfullscreen
            style="width:100%;height:100%;"
          ></iframe>
        `;
        } else {
            console.error('无法加载视频: 缺少必要的参数');
            return;
        }

        dialog.showModal();
    }

    disconnectedCallback() {
        if (this.mainSwiper) {
            this.mainSwiper.destroy();
            this.mainSwiper = null;
        }
        if (this.thumbSwiper) {
            this.thumbSwiper.destroy();
            this.thumbSwiper = null;
        }
    }
}
customElements.define('product-media-gallery-v2', ProductMediaGallery);


// 产品优惠券卡片组件
class ProductCouponCard extends HTMLElement {
    constructor() {
        super();
        this.copyButton = this.querySelector('.product__coupon-card-btn');
        this.couponCode = this.dataset.couponCode;
        this.copiedText = this.copyButton.dataset.copied;
        this.copiedToast = this.copyButton.dataset.toast;

        if (this.copyButton) {
            this.copyButton.addEventListener('click', this.copyCode.bind(this));
        }
    }

    copyCode() {
        const fallbackCopy = () => {
            const textarea = document.createElement('textarea');
            textarea.value = this.couponCode;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);

            textarea.select();

            try {
                const successful = document.execCommand('copy');
                if (!successful) {
                    throw new Error('后备复制方法失败');
                }
                return true;
            } catch (err) {
                console.error('后备复制方法失败:', err);
                return false;
            } finally {
                document.body.removeChild(textarea);
            }
        };

        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(this.couponCode).then(() => {
                this.showCopiedFeedback();
                this.applyDiscount();
            }).catch(() => {
                if (fallbackCopy()) {
                    this.showCopiedFeedback();
                    this.applyDiscount();
                }
            });
        } else {
            if (fallbackCopy()) {
                this.showCopiedFeedback();
                this.applyDiscount();
            }
        }
    }

    showCopiedFeedback() {
        const originalHTML = this.copyButton.innerHTML;

        this.copyButton.innerHTML = `
           <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M13.8536 3.85355C14.0488 3.65829 14.0488 3.34171 13.8536 3.14645C13.6583 2.95118 13.3417 2.95118 13.1464 3.14645L6.5 9.79289L3.85355 7.14645C3.65829 6.95118 3.34171 6.95118 3.14645 7.14645C2.95118 7.34171 2.95118 7.65829 3.14645 7.85355L6.14645 10.8536C6.34171 11.0488 6.65829 11.0488 6.85355 10.8536L13.8536 3.85355Z" fill="#1A1A1A"/>
            </svg>
            ${this.copiedText}
         `;

        const copyToast = document.createElement('div');
        copyToast.className = 'copied-toast';
        copyToast.innerHTML = `${this.copiedToast}`;
        document.body.appendChild(copyToast);
        setTimeout(() => {
            this.copyButton.innerHTML = originalHTML;
            copyToast.remove();
        }, 3000);
    }

    applyDiscount() {
      fetch(`/discount/${this.couponCode}`);
    }
}
customElements.define('product-coupon-card', ProductCouponCard);


// 颜色变体选择器
class ProductColorSwatches extends HTMLElement {
  constructor() {
    super();
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('.color-swatch-input').forEach(input => {
      input.addEventListener('change', () => {
        publish('variant:option-changed', {
          optionName: 'option1',
          optionValue: input.value
        });
      });
    });
  }
}
customElements.define('product-color-swatches', ProductColorSwatches);


// 其他变体选择器
class ProductVariantPickerV2 extends HTMLElement {
  constructor() {
    super();
    this.bindEvents();
  }

  bindEvents() {
    const optionGroups = this.querySelectorAll('.variant-picker-option');
    
    optionGroups.forEach((group, index) => {
      const inputs = group.querySelectorAll('input[type="radio"]');
      inputs.forEach(input => {
        input.addEventListener('change', () => {
          const optionPosition = this.dataset.colorHandled === 'true' ? index + 2 : index + 1;
          
          publish('variant:option-changed', {
            optionName: `option${optionPosition}`,
            optionValue: input.value
          });
        });
      });
    });
  }
}
customElements.define('product-variant-picker-v2', ProductVariantPickerV2);


// 互补产品
class productComplementaryPickerV2 extends HTMLElement {
  constructor() {
    super();
    this.complementaryList = []; // 存储所有配套品信息：{isChecked, quantity, basePrice, baseComparePrice}
    this.swiperInstance = null;
  }

  connectedCallback() {
    this.handleIntersection();
    this.addEventListener('change', this.handleAllChange.bind(this));
  }

  handleIntersection() {
    fetch(this.dataset.url)
      .then((response) => {
        if (!response.ok) throw new Error('接口请求失败');
        return response.text();
      })
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-complementary-picker-v2");

        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }
      })
      .catch((e) => {
        console.error('配套品加载失败：', e);
      }).finally(()=> {
        this.initSwipers();
        this.initComplementaryData();
      })
  };

  initSwipers() {
    if (this.swiperInstance) this.swiperInstance.destroy(true, true);
    this.swiperInstance = new Swiper(this.querySelector('.complementary-products-container'), {
      navigation: {
          nextEl: '.complementary-swiper-button-next',
          prevEl: '.complementary-swiper-button-prev',
      },
      pagination: {
        el: this.querySelector('.complementary-swiper-pagination'),
        clickable: true,
      },
    });
  }

  initComplementaryData() {
    this.cards = [...this.querySelectorAll('.complementary-product-card')];
    this.complementaryList = this.cards.map(card => {
      const quantityInput = card.querySelector('.complementary-quantity__input');
      const checkBox = card.querySelector('.complementary-checkbox');
      return {
        isChecked: checkBox?.checked || false, // 初始选中状态
        quantity: parseInt(quantityInput?.value) || 0, // 初始数量
        basePrice: parseInt(quantityInput?.dataset.price) || 0, // 配套品单价
        baseComparePrice: parseInt(quantityInput?.dataset.comparePrice) || parseInt(quantityInput?.dataset.price) || 0, // 配套品划线价
        checkBox, // 保存DOM引用，方便后续操作
        quantityInput, // 保存DOM引用
        variantId: quantityInput?.dataset.variantId
      };
    });
    // 初始化后立即发布一次配套品价格事件，让计算器同步
    this.publishComplementaryPrice();
  }

  handleAllChange(e) {
    const target = e.target;
    // 匹配「卡片选中checkbox」
    if (target.matches('.complementary-checkbox')) {
      this.updateCheckedStatus(target);
    }
    // 匹配「配套品数量input」
    if (target.matches('.complementary-quantity__input')) {
      this.updateQuantity(target);
    }
    // 每次变化都发布价格事件
    this.publishComplementaryPrice();
  }

  // 更新卡片选中状态
  updateCheckedStatus(checkbox) {
    // 找到当前checkbox对应的配套品索引
    const index = this.complementaryList.findIndex(item => item.checkBox === checkbox);
    if (index > -1) {
      this.complementaryList[index].isChecked = checkbox.checked;
    }
  }

  // 更新配套品数量（做非数字/最小值校验）
  updateQuantity(input) {
    const index = this.complementaryList.findIndex(item => item.quantityInput === input);
    if (index > -1) {
      const quantity = parseInt(input.value) || parseInt(input.dataset.min);
      this.complementaryList[index].quantity = quantity < 1 ? parseInt(input.dataset.min) : quantity;
      input.value = this.complementaryList[index].quantity; // 同步回输入框，避免显示0/负数
    }
  }

  // 计算配套品总价并发布事件
  publishComplementaryPrice() {
    // 计算所有「选中的」配套品的总价/总划线价
    const total = this.complementaryList.reduce((acc, item) => {
      if (item.isChecked) {
        acc.price += item.basePrice * item.quantity;
        acc.compare_price += item.baseComparePrice * item.quantity;
        acc.quantity += item.quantity; // 配套品总数量

        acc.checkedItems.push({
          quantity: item.quantity,
          basePrice: item.basePrice,
          baseComparePrice: item.baseComparePrice,
          variantId: item.variantId
        });
      }
      return acc;
    }, { quantity: 0, price: 0, compare_price: 0, checkedItems: [] });

    publish('complementary:price-changed', total);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleAllChange.bind(this));
    if (this.swiperInstance) this.swiperInstance.destroy(true, true);
  }
}
customElements.define("product-complementary-picker-v2", productComplementaryPickerV2);


// 主产品数量选择器组件
class ProductQuantitySelector extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="number"]');
    this.variantId =  this.input.dataset.variantId;
    this.quantity = parseInt(this.input?.value) || 0;
    this.basePrice = parseInt(this.input?.dataset.price) || 0; 
    this.baseComparePrice = parseInt(this.input?.dataset.comparePrice) || 0;
    this.price = this.basePrice * this.quantity;
    this.compare_price = this.baseComparePrice * this.quantity;
  }

  connectedCallback() {
    this.handleChange();
    this.input?.addEventListener('change', this.handleChange.bind(this));
  }

  handleChange() {
    let { input, quantity, basePrice, price, baseComparePrice, compare_price } = this;
    if(parseInt(input.value) < parseInt(input.dataset.min)) {
      input.value = parseInt(input.dataset.min);
    }
    quantity = parseInt(input.value) || parseInt(input.dataset.min);
    price = basePrice * quantity;
    compare_price = baseComparePrice * quantity;
    
    setTimeout(() => {
      publish('main:quantity-changed', {
        variantId: this.variantId,
        quantity: quantity,
        price: price,
        compare_price: compare_price
      });
    }, 0);
  }

  disconnectedCallback() {
    if (this.input) {
      this.input.removeEventListener('change', this.handleChange.bind(this));
    }
  }
}
customElements.define('product-quantity-selector', ProductQuantitySelector);


// 商品总价计算组件
class ProductPricecalCulator extends HTMLElement {
  constructor() {
    super();
    this.priceEl = this.querySelector('[data-total-price]') || this;
    this.comparePriceEl = this.querySelector('[data-total-compare-price]');
    this.main_quantity = null;
    this.main_quantity_data = null;
    this.complementary_price = null;
    this.complementary_price_data = null;
  }

  connectedCallback() {
    this.main_quantity = subscribe('main:quantity-changed', (data) => {
      this.main_quantity_data = data;
      this.updatePrice();
    });

    this.complementary_price = subscribe('complementary:price-changed', (data) => {
      this.complementary_price_data = data;
      this.updatePrice();
    });
  }

  // 更新价格展示
  updatePrice() {
    const { main_quantity_data, complementary_price_data } = this;
    const price = (main_quantity_data?.price || 0) + (complementary_price_data?.price || 0);
    const comparePrice = (main_quantity_data?.compare_price || 0) + (complementary_price_data?.compare_price || 0);

    this.priceEl.textContent = formatMoney(price);
    this.comparePriceEl.textContent = comparePrice > price ? formatMoney(comparePrice) : "";
  }

  disconnectedCallback() {
    // 取消事件订阅
    if (this.main_quantity) {
      unsubscribe('main:quantity-changed', this.main_quantity);
    }
    if (this.complementary_price) {
      unsubscribe('complementary:price-changed', this.complementary_price);
    }
  }
}
customElements.define('product-price-calculator', ProductPricecalCulator);


// 顶部固定栏
class TopFixedBar extends HTMLElement {
  constructor() {
    super();
    this.navItems = [];
    this.sections = [];
    this.currentActiveIndex = -1;
    this.scrollType = this.dataset.scrollType;
    this.isScrolling = false;
    this.scrollTimeout = null;
    this.stickyHeader = document.querySelector('sticky-header');
    this.productPrice = document.querySelector('.product__price');
    this.productInfoV2 = document.querySelector('product-info-v2');
  }

  connectedCallback() {
    this.initNavigation();
    this.setupScrollListener();
    this.updateStickyHeaderMetrics(); 
    this.updateSectionMetrics();
  }

  // 初始化导航
  initNavigation() {
    this.navItems = Array.from(this.querySelectorAll('.top-fixed-bar__nav-item'));
    
    // 收集有效区块
    this.sections = this.navItems.map(item => {
      const selector = item.dataset.navClassOrId?.trim();
      return selector ? document.querySelector(selector) : null;
    }).filter(Boolean);

    this.addEventListener('click', (e) => {
      const navItem = e.target.closest('.top-fixed-bar__nav-item');
      if (navItem) {
        const index = this.navItems.indexOf(navItem);
        if (index !== -1) this.scrollToSection(index);
      }
    });
  }

  // 设置滚动监听
  setupScrollListener() {
    this.debouncedUpdateActiveNavItem = debounce(() => this.updateActiveNavItem(), 50);
    this.debouncedUpdateSectionMetrics = debounce(() => this.updateSectionMetrics(), 50);

    window.addEventListener('scroll', () => {
      if (!this.isScrolling) this.debouncedUpdateActiveNavItem();
      this.updateStickyHeaderMetrics();
      this.debouncedUpdateSectionMetrics();
    });

    setTimeout(() => {
      this.debouncedUpdateActiveNavItem();
      this.updateStickyHeaderMetrics();
      this.updateSectionMetrics();
    }, 100);
  }

  // 更新活动导航项
  updateActiveNavItem() {
    const scrollPosition = window.scrollY + this.offsetHeight;
    
    let activeIndex = 0;
    for (let i = this.sections.length - 1; i >= 0; i--) {
      if (this.sections[i].offsetTop <= scrollPosition) {
        activeIndex = i;
        break;
      }
    }
    
    if (activeIndex !== this.currentActiveIndex) {
      this.setActiveNavItem(activeIndex);
    }
  }

  // 滚动到指定区块
  scrollToSection(index) {
    const targetSection = this.sections[index];
    if (!targetSection) return;
    
    this.isScrolling = true;
    const targetPosition = targetSection.offsetTop - this.offsetHeight;
    
    if(this.scrollType == "smooth") {
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    } else {
      const originalBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo({ top: targetPosition });
    }

    setTimeout(() => {
      this.isScrolling = false;
      this.setActiveNavItem(index);
    }, 500);
  }

  // 设置活动导航项
  setActiveNavItem(index) {
    this.navItems.forEach((item, i) => {
      item.classList.toggle('active', i === index);
    });
    this.currentActiveIndex = index;
  }

  updateSectionMetrics() {
    // 1️⃣ product__price
    if (this.productPrice) {
      const elementTop = this.productPrice.offsetTop;
      const isScrolledPast = window.scrollY > elementTop;
      this.classList.toggle('mobile-visible-top-fixed-bar__right', isScrolledPast);
    }

    // 2️⃣ product-info-v2
    if (this.productInfoV2) {
      const elementTop = this.productInfoV2.offsetTop + this.productInfoV2.offsetHeight;
      const isScrolledPast = window.scrollY > elementTop;
      this.classList.toggle('mobile-visible-top-fixed-bar__nav', isScrolledPast);
    }
  }

  updateStickyHeaderMetrics() {
    if (!this.stickyHeader) return;
    const rect = this.stickyHeader.getBoundingClientRect();
    if((rect.top + rect.height) >= 0 ) {
      this.style.setProperty('--sticky-header-top', `${rect.top}px`);
    }else {
      this.style.setProperty('--sticky-header-top', `-${rect.height}px`);
    }
    // this.style.setProperty('--sticky-header-top', `${rect.top}px`);
    this.style.setProperty('--sticky-header-height', `${rect.height}px`);
  }

  disconnectedCallback() {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  }
}
customElements.define('top-fixed-bar', TopFixedBar);


function formatMoney(cents, format) {
  // 默认格式和配置
  const defaultFormat = '${{amount}}';
  const formatString = format || window.theme?.moneyFormat || defaultFormat;
  
  // 清理输入：如果是字符串，移除小数点
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  
  // 验证输入
  if (isNaN(cents) || cents === null) {
    return '0';
  }
  
  // 解析格式模板中的占位符
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatType = formatString.match(placeholderRegex)?.[1] || 'amount';
  
  // 格式化数字的核心函数
  const formatWithDelimiters = (number, precision, thousands = ',', decimal = '.') => {
    // 转换为小数并固定精度
    const amount = (number / 100).toFixed(precision);
    const [dollars, centsPart] = amount.split('.');
    
    // 添加千位分隔符
    const formattedDollars = dollars.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    
    // 处理小数部分
    const formattedCents = centsPart ? `${decimal}${centsPart}` : '';
    
    return formattedDollars + formattedCents;
  };
  
  // 根据格式类型选择格式化方式
  const formatHandlers = {
    amount: () => formatWithDelimiters(cents, 2),
    amount_no_decimals: () => formatWithDelimiters(cents, 0),
    amount_with_comma_separator: () => formatWithDelimiters(cents, 2, '.', ','),
    amount_no_decimals_with_comma_separator: () => formatWithDelimiters(cents, 0, '.', ','),
    amount_no_decimals_with_space_separator: () => formatWithDelimiters(cents, 0, ' '),
    amount_with_apostrophe_separator: () => formatWithDelimiters(cents, 2, "'")
  };
  
  // 执行格式化并替换模板中的占位符
  const formattedValue = formatHandlers[formatType]?.() || formatWithDelimiters(cents, 2);
  
  return formatString.replace(placeholderRegex, formattedValue);
}


// 防抖函数
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}